import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserCircle, FaSignOutAlt, FaSyncAlt, FaChevronDown } from 'react-icons/fa';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import './UserMenu.css';

// Header user menu — renders in AppHeader next to NotificationBell.
//   [user pill ▼]  [Logout]
// The pill opens a dropdown with user details + Refresh permissions.
// Logout is its own standalone button next to the pill.
function UserMenu() {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!user) return null;

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setOpen(false);
    navigate('/');
  };

  const refreshPermissions = async () => {
    try {
      const res = await axios.get('/users/me');
      const fresh = res?.data?.user;
      if (fresh && fresh.id) {
        try { localStorage.setItem('user', JSON.stringify(fresh)); } catch {}
        setUser(fresh);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('refreshPermissions failed:', err?.message || err);
    }
  };

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');

  return (
    <div className="user-menu-group">
      {/* User pill — dropdown */}
      <div className="user-menu" ref={wrapRef}>
        <button
          type="button"
          className="user-menu__trigger"
          onClick={() => setOpen(v => !v)}
          title={user.email}
          aria-expanded={open}
        >
          <FaUserCircle className="user-menu__icon" />
          <span className="user-menu__email">{user.email}</span>
          <FaChevronDown className={`user-menu__chevron${open ? ' user-menu__chevron--open' : ''}`} />
        </button>

        {open && (
          <div className="user-menu__panel" role="menu">
            <div className="user-menu__header">
              <FaUserCircle className="user-menu__header-icon" />
              <div className="user-menu__header-text">
                <div className="user-menu__name">{fullName || user.email}</div>
                <div className="user-menu__sub">{user.email}</div>
                {user.organisation_name && (
                  <div className="user-menu__sub">{user.organisation_name}</div>
                )}
                {user.is_system_admin && (
                  <div className="user-menu__badge">System Admin</div>
                )}
              </div>
            </div>
            <button
              type="button"
              className="user-menu__item"
              onClick={() => { refreshPermissions(); setOpen(false); }}
              title="Reload your permissions from the server"
            >
              <FaSyncAlt /> Refresh permissions
            </button>
          </div>
        )}
      </div>

      {/* Standalone Logout button (no dropdown) */}
      <button
        type="button"
        className="user-menu__logout"
        onClick={handleLogout}
        title="Sign out"
      >
        <FaSignOutAlt />
        <span className="user-menu__logout-label">Logout</span>
      </button>
    </div>
  );
}

export default UserMenu;
